import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * 按钮变体
   * - primary: 烫金渐变主按钮
   * - secondary: 边框烫金次要按钮
   * - text: 文本按钮
   */
  variant?: 'primary' | 'secondary' | 'text';
  /**
   * 按钮尺寸
   * - sm: 小尺寸
   * - md: 中尺寸 (默认)
   * - lg: 大尺寸
   * - xl: 超大尺寸
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * 是否显示加载状态
   */
  loading?: boolean;
  /**
   * 加载状态文本
   */
  loadingText?: string;
  /**
   * 是否全宽
   */
  fullWidth?: boolean;
  /**
   * 左侧图标
   */
  leftIcon?: React.ReactNode;
  /**
   * 右侧图标
   */
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText = '处理中...',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...props
}) => {
  // 基础样式类
  const baseClasses = 'btn inline-flex items-center justify-center font-medium transition-all duration-200 focus-visible focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:opacity-60 disabled:cursor-not-allowed';
  
  // 变体样式类
  const variantClasses = {
    primary: 'btn-primary text-brand-primary font-semibold hover:shadow-gold active:scale-95',
    secondary: 'btn-secondary text-brand-accent border border-brand-accent hover:bg-brand-accent/10 active:scale-95',
    text: 'btn-text text-brand-accent hover:bg-brand-accent/5 active:bg-brand-accent/10',
  };
  
  // 尺寸样式类
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm rounded',
    md: 'px-4 py-2.5 text-base rounded-md',
    lg: 'px-6 py-3 text-lg rounded-lg',
    xl: 'px-8 py-4 text-xl rounded-lg',
  };
  
  // 全宽样式
  const widthClass = fullWidth ? 'w-full' : '';
  
  // 组合所有样式类
  const buttonClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClass,
    className
  ].filter(Boolean).join(' ');
  
  return (
    <button
      className={buttonClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {loadingText}
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;